import React, { useRef, useState } from 'react';
import Messages from './Messages';
import Input from './Input';
import { useDispatch, useSelector } from 'react-redux';
import { changeIsUserSelected } from '../../reducers/wca/sales-support/sales-support-slice';
import { initiateCall as initiateCallAction } from '../../reducers/wca/sales-support/sales-support-action';
import Products from './Products';
import { Toast } from 'primereact/toast';
import { CustomButton } from '../../components/CustomButton';
import { Text } from '../../components/Text';
import FileInputsComponenets from '../../components/FileInputsComponentsWCA';
import AgentPlace from './AgentPlace';
import TicketReasign from './TicketReasign';
import { formatMobNumber } from '../../utils/commonUtils';
import { Menu } from 'primereact/menu';
import modulesName from '../../roles/modulesName';
import permissions from '../../roles/permissions';
import { ReactComponent as AddToGroupIcon } from './../../svg/addToGroupIcon.svg';

import CopyToClipboard from '../../components/CopyToClipboard';
import ChatControlMenu from './components/chat-control-menu';
import { Button } from 'primereact/button';
import { useUtility } from '../../context/UtilityContext';
import { useCall } from '../../hooks/useCallSocket';
import {
  canBusinessInitiateCall,
  formatCallPermissionTimestamp,
  getCallPermissionExpiryTimestamp,
} from '../../utils/wcaCallPermission';

const VALID_PHONE_REGEX = /^\d{10,15}$/;
const ACTIVE_CALL_STATUSES = new Set(['initiating', 'calling', 'ringing', 'connecting', 'connected']);
const DEVELOPMENT_MODE = process.env.NODE_ENV === 'development';

const Chat = ({ setAddToGroupSliderOpen }) => {
  const { user, isFileSelect, isProductSelect, isImageSelect, isVideoSelect } = useSelector(
    (state) => state.wcaEnqChats,
  );
  const { accesskey, user: User } = useSelector((state) => state.authenticate);
  const { metadataList } = useSelector((state) => state.metadata);
  const dispatch = useDispatch();
  const toast = useRef(null);
  const menuButtonRef = useRef(null);
  const initiateCallGuardRef = useRef(false);
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const mobNumber = formatMobNumber(user?.phone, true);
  const { selectedMetadataId } = useUtility();
  const {
    activeCall,
    startOutgoingCall,
    createOutgoingOffer,
    updateActiveCall,
    emitCallEvent,
    getSocketId,
    markCallFailed,
    disconnectPeer,
  } = useCall();

  let items = [
    {
      label: mobNumber,
      icon: 'pi pi-copy',
      command: () => {
        navigator.clipboard.writeText(mobNumber);
        toast.current.show({ severity: 'success', detail: 'Copied on clipboard ', life: 3000 });
      },
    },
  ];

  function ChatHeader() {
    return (
      <div className="flex align-items-center gap-1">
        <CustomButton
          varient="icon-button md:hidden"
          icon={'pi pi-arrow-left'}
          onClick={() => dispatch(changeIsUserSelected(false))}
        />
        <Text type="sub-heading" cust="flex align-items-center">
          <div className="capitalize mr-3">
            <Text type={'sub-heading'}>{user?.whatsappName || mobNumber}</Text>
          </div>
          <div>
            {user?.businessName ? (
              <Button
                text
                rounded
                className="h-auto"
                icon={'pi pi-info-circle'}
                onClick={(event) => menuButtonRef.current.toggle(event)}
              />
            ) : (
              <CopyToClipboard toClipboard={mobNumber} />
            )}
          </div>
        </Text>
      </div>
    );
  }
  function fixPhoneNumber(phone) {
    if (!phone) return '';

    return decodeURIComponent(phone).replace(/\D/g, '');
  }

  const callPermission = canBusinessInitiateCall(user);
  const permissionExpiryTimestamp = getCallPermissionExpiryTimestamp(user);
  const permissionExpiryLabel = formatCallPermissionTimestamp(permissionExpiryTimestamp);
  const callPermissionTooltip =
    user?.callPermissionIsPermanent === true
      ? 'Call permission: Permanent'
      : permissionExpiryLabel
        ? `Call permission till: ${permissionExpiryLabel}`
        : callPermission.message;
  function getCallId(callData = {}) {
    return (
      callData?.msgId ||
      callData?.callId ||
      callData?.id ||
      callData?.message?.id ||
      callData?.message?.session?.id ||
      callData?.message?.session?.callId ||
      null
    );
  }

  function normalizeCallResponse(response) {
    return response?.data || response?.message || response || {};
  }

  function getCallFailureMessage(error) {
    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      return 'Microphone permission is required to start a call.';
    }

    return error?.message || error?.detail || 'Failed to initiate call';
  }

  function logCallDebug(label, payload) {
    if (!DEVELOPMENT_MODE) return;
    console.log(`[WCA Call] ${label}`, payload);
  }

  function getMetadataBusinessPhone() {
    const currentMetadata = metadataList?.find(
      (item) => String(item?.id) === String(selectedMetadataId),
    );

    return fixPhoneNumber(
      currentMetadata?.displayPhoneNumber ||
      currentMetadata?.display_phone_number ||
      currentMetadata?.phone ||
      currentMetadata?.phoneNumber ||
      '',
    );
  }

  const initiateCall = async () => {
    if (initiateCallGuardRef.current) {
      return;
    }

    const currentActiveStatus = activeCall?.status;
    if (activeCall?.isOpen && ACTIVE_CALL_STATUSES.has(currentActiveStatus)) {
      toast.current?.show({
        severity: 'warn',
        detail: currentActiveStatus === 'connected' ? 'A call is already in progress.' : 'A call is already being placed.',
        life: 3000,
      });
      return;
    }

    if (!selectedMetadataId) {
      toast.current?.show({
        severity: 'warn',
        detail: 'WhatsApp account is not selected for calling.',
        life: 3000,
      });
      return;
    }

    if (!user || Object.keys(user).length === 0) {
      toast.current?.show({
        severity: 'warn',
        detail: 'Contact details are not available for this call.',
        life: 3000,
      });
      return;
    }

    const phoneNumber = fixPhoneNumber(user?.phone || mobNumber);
    const businessPhone = getMetadataBusinessPhone();
    const isBlockedContact = user?.isBlocked === true || !!user?.isOptOut;
    const permission = canBusinessInitiateCall(user);

    if (!phoneNumber) {
      toast.current?.show({
        severity: 'warn',
        detail: 'Customer phone number is not available for calling.',
        life: 3000,
      });
      return;
    }

    if (!navigator.onLine) {
      toast.current?.show({
        severity: 'error',
        detail: 'No internet connection available for calling',
        life: 4000,
      });
      return;
    }

    if (!VALID_PHONE_REGEX.test(phoneNumber)) {
      toast.current?.show({
        severity: 'warn',
        detail: 'Invalid phone number for calling',
        life: 4000,
      });
      return;
    }

    if (businessPhone && businessPhone === phoneNumber) {
      toast.current?.show({
        severity: 'warn',
        detail: 'You cannot place a call to the same business number',
        life: 4000,
      });
      return;
    }

    if (isBlockedContact) {
      toast.current?.show({
        severity: 'warn',
        detail: 'This contact is blocked. Unblock before placing a call.',
        life: 4000,
      });
      return;
    }

    if (!permission.canCall) {
      toast.current?.show({
        severity: 'warn',
        detail: permission.message,
        life: 4000,
      });
      return;
    }

    try {
      initiateCallGuardRef.current = true;
      setIsInitiatingCall(true);

      let sdpOffer = '';
      try {
        sdpOffer = await createOutgoingOffer();
      } catch (error) {
        throw error;
      }

      if (!sdpOffer) {
        throw new Error('Call setup failed because no audio session could be created.');
      }

      const initiatedSocketId = getSocketId();
      if (!initiatedSocketId) {
        throw new Error('Call service is not connected. Please try again.');
      }

      startOutgoingCall({ phone: phoneNumber });

      const payload = {
        phone: phoneNumber,
        metadataId: Number(selectedMetadataId),
        initiatedSocketId,
        sdpOffer,
      };
      logCallDebug('initiate payload', payload);
      const response = await dispatch(
        initiateCallAction({
          metadataid: selectedMetadataId,
          phone: phoneNumber,
          data: payload,
        }),
      ).unwrap();
      logCallDebug('initiatecall API response', response);
      const responseData = normalizeCallResponse(response);
      const callId = getCallId(responseData);
      const msgId = responseData?.msgId || callId;
      if (!callId && !msgId && DEVELOPMENT_MODE) {
        console.warn('[WCA Call] initiatecall API succeeded without callId/msgId', responseData);
      }

      updateActiveCall((prev) => ({
        status: 'calling',
        error: '',
        callData: {
          ...(prev.callData || {}),
          ...responseData,
          msgId,
          sdpOffer,
        },
      }));

      logCallDebug('emit call_initiate', {
        phone: phoneNumber,
        metadataId: Number(selectedMetadataId),
        initiatedSocketId,
        sdpOffer,
        callId,
        msgId,
      });
      emitCallEvent('call_initiate', {
        phone: phoneNumber,
        metadataId: Number(selectedMetadataId),
        initiatedSocketId,
        sdpOffer,
        callId,
        msgId,
      });
    } catch (err) {
      console.error('Call initiate failed', err);
      disconnectPeer();
      markCallFailed(getCallFailureMessage(err));
      toast.current?.show({
        severity: 'error',
        detail: getCallFailureMessage(err),
        life: 4000,
      });
    } finally {
      initiateCallGuardRef.current = false;
      setIsInitiatingCall(false);
    }
  };

  const isSuperAdmin = User?.role === 'superadmin';
  const isCallDisabled =
    !callPermission.canCall ||
    isInitiatingCall ||
    ACTIVE_CALL_STATUSES.has(activeCall?.status);
  const callButtonLabel = !callPermission.canCall
    ? 'Call Block'
    : activeCall?.status === 'connected'
      ? 'On Call'
      : activeCall?.status === 'ringing'
        ? 'Ringing...'
        : isInitiatingCall || activeCall?.status === 'initiating' || activeCall?.status === 'calling' || activeCall?.status === 'connecting'
          ? 'Calling...'
          : 'Call';
  return (
    <div className="h-full">
      <Toast ref={toast} />
      <Menu model={items} popup ref={menuButtonRef} id="menu_button" />
      <div className="flex flex-column h-full ">
        {!accesskey[modulesName.SALES]?.includes(permissions.UPDATE) && <AgentPlace />}
        {Object.keys(user)?.length !== 0 && (
          <>
            <div className="chatInfo p-2">
              <div className="flex flex-column md:flex-row justify-content-between  align-items-start md:align-items-center">
                <div className="w-auto">{ChatHeader()}</div>
                <div className="flex justify-content-end align-items-center flex-grow-1">
                  <TicketReasign />
                  {accesskey[modulesName.CUSTOMER]?.includes(permissions.CREATE) && (
                    <Button
                      text
                      rounded
                      className="ml-1"
                      label={callButtonLabel}
                      icon={callPermission.canCall ? 'pi pi-phone' : 'pi pi-ban'}
                      disabled={isCallDisabled}
                      tooltip={callPermissionTooltip}
                      tooltipOptions={{ position: 'bottom' }}
                      onClick={initiateCall}
                    />
                  )}
                  {accesskey[modulesName.CUSTOMER]?.includes(permissions.CREATE) && (
                    <Button
                      text
                      rounded
                      className="ml-1"
                      icon={<AddToGroupIcon />}
                      onClick={() => setAddToGroupSliderOpen((prev) => !prev)}
                    />
                  )}
                  {isSuperAdmin && <ChatControlMenu chatInfo={user} />}
                </div>
              </div>
            </div>

            {isFileSelect ? (
              <FileInputsComponenets />
            ) : isImageSelect ? (
              <FileInputsComponenets />
            ) : isVideoSelect ? (
              <FileInputsComponenets />
            ) : isProductSelect ? (
              <Products />
            ) : (
              <>
                <Messages />
                <Input />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Chat;
